import React, {memo, useState, useEffect, useRef, KeyboardEvent, MouseEvent} from 'react';
import {s} from "@/app/utils/s";
import {t} from "i18next";
import Input from "@/app/components/input";
import styles from "./styles.module.scss";
import LazyAssets from "@/app/utils/assets_widget";
import {TextMedium16} from "@/app/components/texts";
import classNames from "classnames";
import useDropdown from "@/app/hooks/useDropdown";

interface Props {
  onCitySelect: (city: { name: string; lat: number; lng: number, fullName: string }) => void;
  className?: string
  value?: string
  isWhite?: boolean
}

const CityAutocomplete: React.FC<Props> = ({
                                             onCitySelect,
                                             className,
                                             value,
                                             isWhite = false
                                           }) => {
  const [inputValue, setInputValue] = useState(value ?? "");
  const [predictions, setPredictions] = useState<google.maps.places.PlacePrediction[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [inputClicked, setInputClicked] = useState(false);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken>();
  const { isOpen, open, close, containerRef } = useDropdown(false, { closeOnEscape: true, closeOnEnter: true });

  const handleGoogleLoad = () => {
    sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
  };

  const showDropdownMenu = (value: boolean) => {
    if (value) open(); else close();
  };

  useEffect(() => {
    if (value) {
      setInputValue(value);
    }
  }, [value]);

  useEffect(() => {
    if (window.google?.maps?.places) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
  }, []);

  useEffect(() => {
    if (inputValue.length < 2 || !inputClicked) {
      setPredictions([]);
      return;
    }
    // Ensure Google Places is loaded (admin subdomain may load script lazily)
    if (!window.google?.maps?.places?.AutocompleteSuggestion || !sessionTokenRef.current) {
      return;
    }
    (async () => {
      try {
        const { suggestions } =
          await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: inputValue,
            sessionToken: sessionTokenRef.current!,
            region: "US",
          });
        const mapped = suggestions.map(s => s.placePrediction);

        const placePredictions = mapped.filter(
          (p): p is google.maps.places.PlacePrediction => p !== null
        );

        setPredictions(placePredictions);
        showDropdownMenu(true)
      } catch (e) {
        console.error('Places autocomplete failed:', e);
      }
    })();
  }, [inputValue, inputClicked]);

  const handleSelect = async (prediction: google.maps.places.PlacePrediction) => {
    const place = prediction.toPlace();
    await place.fetchFields({fields: ['displayName', 'location']});
    const name = place.displayName ?? "";
    onCitySelect({
      name: place.displayName ?? "",
      lat: place.location!.lat(),
      lng: place.location!.lng(),
      fullName: prediction.text.text
    });
    setInputValue(name);
    setPredictions([]);
    showDropdownMenu(false);
    setInputClicked(false);

    // Remove focus from input
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!predictions.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(predictions.length - 1, i + 1));
      scrollToActive();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(-1, i - 1));
      scrollToActive();
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(predictions[activeIndex]);
    }
  };

  const scrollToActive = () => {
    const ul = dropdownRef.current;
    if (!ul || activeIndex < 0) return;
    const li = ul.children[activeIndex] as HTMLElement;
    const top = li.offsetTop, bottom = top + li.offsetHeight;
    if (bottom > ul.scrollTop + ul.clientHeight) {
      ul.scrollTop = bottom - ul.clientHeight;
    } else if (top < ul.scrollTop) {
      ul.scrollTop = top;
    }
  };

  return (
    <div ref={containerRef as React.RefObject<HTMLDivElement>} className={classNames(styles.wrapper, className)}>
      <Input
        hint={t(s.search)}
        value={inputValue}
        onChange={setInputValue}
        onKeyDown={onKeyDown}
        prefixIcon={<LazyAssets.Search/>}
        isWhite
        onClick={() => {
          setInputClicked(true);
          showDropdownMenu(true);
        }}
      />
      {isOpen && predictions.length > 0 && (
        <ul
          ref={dropdownRef}
          className={classNames(styles.dropdown, {[styles.white]: isWhite})}
        >
          {predictions.map((p, idx) => (
            <li
              key={p.placeId}
              onMouseDown={(e: MouseEvent) => {
                e.preventDefault();
                handleSelect(p);
              }}
              onMouseEnter={() => setActiveIndex(idx)}
              className={styles.dropdownItem}
            >
              <TextMedium16 text={p.text.text}/>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default memo(CityAutocomplete);